import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TokenContext } from "@context/TokenContext";
import { useTranslation } from "@hooks/useTranslation";
import { LanguageCurrencyContext } from "@context/LanguageCurrencyContext";
import { useAuth } from "@context/AuthContext";
import axios from "@fetch/axios";
import Spinner from "@components/Spinner";
import Swal from "sweetalert2";
import withReactContent from 'sweetalert2-react-content'

const ComprarComoInvitado = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { lang ,currency} = useContext(LanguageCurrencyContext);
  const { getToken,getVerified } = useContext(TokenContext);
  const { openAuth } = useAuth();
  const [loading, setLoading] = useState(true)

  const [name, setName] = useState(localStorage.getItem('guest_checkout_name') || '');
  const [lastname, setLastname] = useState(localStorage.getItem('guest_checkout_lastname') || '');
  const [email, setEmail] = useState(localStorage.getItem('guest_checkout_email') || '');
  const [phone, setPhone] = useState(localStorage.getItem('guest_checkout_phone') || '');
  const [nameError, setNameError] = useState('');
  const [lastnameError, setLastnameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const MySwal = withReactContent(Swal)

  // Expresión regular para validar formato de email
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Función para sanitizar el nombre - solo permite letras, espacios y algunos caracteres especiales
  const sanitizeName = (value) => {
    // Permite letras, espacios, acentos y algunos caracteres comunes en nombres
    const sanitized = value.replace(/[^a-zA-ZñÑáéíóúÁÉÍÓÚüÜ\s'-]/g, '');
    return sanitized;
  };

  // Función para sanitizar el email - solo permite caracteres válidos para email
  const sanitizeEmail = (value) => {
    // Eliminar caracteres peligrosos: solo permite letras, números y caracteres específicos de email
    const sanitized = value.replace(/[^a-zA-Z0-9@._%+-]/g, '');
    return sanitized;
  };

  const sanitizePhone = (value) => {
    return value.replace(/\D/g, '').slice(0, 10);
  };

  // Función para validar el nombre
  const validateName = (value) => {
    if (!value || value.trim().length === 0) {
      setNameError(lang === 'es' 
        ? 'El nombre es requerido' 
        : 'Name is required');
      return false;
    }

    if (value.trim().length < 2) {
      setNameError(lang === 'es' 
        ? 'El nombre debe tener al menos 2 caracteres' 
        : 'Name must be at least 2 characters');
      return false;
    }

    setNameError('');
    return true;
  };
  const validateLastname = (value) => {
    if (!value || value.trim().length === 0) {
      setLastnameError(lang === 'es' 
        ? 'El apellido es requerido' 
        : 'Lastname is required');
      return false;
    }

    if (value.trim().length < 2) {
      setLastnameError(lang === 'es' 
        ? 'El apellido debe tener al menos 2 caracteres' 
        : 'Lastname must be at least 2 characters');
      return false;
    }

    setLastnameError('');
    return true;
  };
  // Función para validar el formato del email
  const validateEmail = (value) => {
    if (!value) {
      setEmailError(lang === 'es' 
        ? 'El email es requerido' 
        : 'Email is required');
      return false;
    }
    
    if (!emailRegex.test(value)) {
      setEmailError(lang === 'es' 
        ? 'Por favor ingresa un email válido' 
        : 'Please enter a valid email');
      return false;
    }

    // Validaciones adicionales de seguridad
    const suspiciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /data:text\/html/i,
      /vbscript:/i
    ];

    for (let pattern of suspiciousPatterns) {
      if (pattern.test(value)) {
        setEmailError(lang === 'es' 
          ? 'Email no válido. Caracteres no permitidos detectados' 
          : 'Invalid email. Suspicious characters detected');
        return false;
      }
    }

    setEmailError('');
    return true;
  };

  const validatePhone = (value) => {
    if (!value) {
      setPhoneError(lang === 'es'
        ? 'El teléfono es requerido'
        : 'Phone is required');
      return false;
    }

    if (!/^\d{10}$/.test(value)) {
      setPhoneError(lang === 'es'
        ? 'El teléfono debe tener exactamente 10 dígitos'
        : 'Phone must be exactly 10 digits');
      return false;
    }

    setPhoneError('');
    return true;
  };

  // Manejar cambio en el input de nombre
  const handleNameChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeName(value);
    setName(sanitizedValue);
    
    if (nameError) {
      setNameError('');
    }
  };

  const handleLastnameChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeName(value);
    setLastname(sanitizedValue);
    
    if (lastnameError) {
      setLastnameError('');
    }
  };

  // Manejar cambio en el input de email
  const handleEmailChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeEmail(value);
    setEmail(sanitizedValue);
    
    if (emailError) {
      setEmailError('');
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = sanitizePhone(value);
    setPhone(sanitizedValue);

    if (phoneError) {
      setPhoneError('');
    }
  };

  // Validar al perder el foco
  const handleNameBlur = () => {
    validateName(name);
  };

  const handleLastnameBlur = () => {
    validateLastname(lastname);
  };
  const handleEmailBlur = () => {
    validateEmail(email);
  };

  const handlePhoneBlur = () => {
    validatePhone(phone);
  };

  // Guardar datos del invitado en localStorage o sessionStorage
  const handleContinue = async (e) => {
    e.preventDefault();

    // Validar ambos campos
    const isNameValid = validateName(name);
    const isLastnameValid = validateLastname(lastname);
    const isEmailValid = validateEmail(email);
    const isPhoneValid = validatePhone(phone);

    if (!isNameValid  || !isLastnameValid || !isEmailValid || !isPhoneValid ) {
        MySwal.fire({
				text: translate('checkout.error_fields'),
				icon: 'error',
				title: 'Oops...'
			})
      return;
    }

    // Sanitizar una vez más antes de guardar
    const finalName = sanitizeName(name).trim();
    const finalLastname = sanitizeName(lastname).trim();
    const finalEmail = sanitizeEmail(email);
    const finalPhone = sanitizePhone(phone);

    localStorage.setItem('guest_checkout_lastname', finalLastname);
    localStorage.setItem('guest_checkout_name', finalName);
    localStorage.setItem('guest_checkout_email', finalEmail);
    localStorage.setItem('guest_checkout_phone', finalPhone);

    try{

      setLoading(false)
      const guest = await axios.post('/users/register-guest', {
        name: finalName,
        lastname: finalLastname,
        email: finalEmail,
        phone: finalPhone,
        lang: lang,
        currency:currency,
        cart: JSON.parse(localStorage.getItem('cart') ?? '[]')
      })
  
      if(guest.data){
        setLoading(true)
        localStorage.setItem('user_arteonu', JSON.stringify(guest.data.user));
        localStorage.setItem('token_arteonu', guest.data.api_token);
        localStorage.setItem('cartId', guest.data.cartId);
        navigate("/checkout-envio");
      }

    }catch(error){
      setLoading(true)

        MySwal.fire({
          text: error.response?.data?.message || translate('error.field'),
          icon: 'error',
          title: 'Oops...'
        })
        return;

    }
   

  };

  // Navegar a iniciar sesión
  const handleLogin = () => {
    openAuth('IniciarSesionWith');
  };

  // Navegar a recuperar contraseña
  const handleRecoverPassword = () => {
    openAuth('RestablecerPassword');
  };

  // Navegar a crear cuenta
  const handleCreateAccount = () => {
    openAuth('CrearCuenta');
  };

  useEffect(() => {
    const totalItem = JSON.parse(localStorage.getItem('cart') || '[]').length;

    if (totalItem === 0) {
      navigate('/home');
      return;
    }

    // Si el usuario ya está logueado, redirigir al checkout de envío
    if (getToken() && getVerified()) {
      navigate("/checkout-envio");
    }
  }, [navigate, getToken, getVerified]);

  return (
    <section className="CheckoutBox">
      {!loading && <Spinner />}

      <article>
        <div className="StepCheckout">
          <div className="Selected"></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
        <div className="icon-IdentificationCard" onClick={() => navigate('/carrito')} style={{ cursor: "pointer" }}></div>

        <div className="IniciarSesion">
          <h5 className="title-guest">{translate('checkout.guest_title')}</h5>

          <form onSubmit={handleContinue} className="DataBox">
            <div className="row">

                <label className={`FormData ${nameError ? 'InvalidInput' : ''}`}>
                  <em className="RequiredData">{translate('checkout.name')}</em>
                  <div className="FormInputLight">
                      <input
                      type="text"
                      value={name}
                      onChange={handleNameChange}
                      onBlur={handleNameBlur}
                      placeholder={translate('checkout.name_placeholder')}
                      required
                      maxLength={100}
                      autoComplete="name"
                      />
                  </div>
                  {nameError && (
                      <em className="InfoData">{nameError}</em>
                  )}
                </label>

                <label className={`FormData ${lastnameError ? 'InvalidInput' : ''}`}>
                  <em className="RequiredData">{translate('checkout.lastname')}</em>
                  <div className="FormInputLight">
                      <input
                      type="text"
                      value={lastname}
                      onChange={handleLastnameChange}
                      onBlur={handleLastnameBlur}
                      placeholder={translate('checkout.lastname_placeholder')}
                      required
                      maxLength={100}
                      autoComplete="lastname"
                      />
                  </div>
                  {nameError && (
                      <em className="InfoData">{nameError}</em>
                  )}
                </label>

                <label className={`FormData ${emailError ? 'InvalidInput' : ''}`}>
                <em className="RequiredData">{translate('checkout.email')}</em>
                <div className="FormInputLight">
                    <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    placeholder={translate('checkout.email_placeholder')}
                    required
                    maxLength={254}
                    autoComplete="email"
                    />
                </div>
                {emailError && (
                    <em className="InfoData">{emailError}</em>
                )}
                </label>

                <label className={`FormData ${phoneError ? 'InvalidInput' : ''}`}>
                <em className="RequiredData">{translate('login.phone')}</em>
                <div className="FormInputLight">
                    <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    placeholder="5500000000"
                    required
                    maxLength={10}
                    inputMode="numeric"
                    autoComplete="tel"
                    />
                </div>
                {phoneError && (
                    <em className="InfoData">{phoneError}</em>
                )}
                </label>

            </div>
            <div className="GroupButton">
              <div className="ButtonDark" onClick={handleContinue}>
                {translate('checkout.continue')}
                <i className="icon-Check"></i>
              </div>

              <div className="ButtonLightMedium" onClick={handleLogin}>
                {translate('checkout.login_button')}
                <i className="icon-ArrowRight"></i>
              </div>
            </div>
          </form>

          <div className="TextColorSec">
            <p>{translate('checkout.forgot_password')}</p>
            <a onClick={handleRecoverPassword} style={{ cursor: 'pointer' }}>
              {translate('checkout.recover_password')}
            </a>
          </div>

          <div className="TextColorSec text">
            <p>{translate('checkout.not_registered')}</p>
            <a onClick={handleCreateAccount} style={{ cursor: 'pointer' }}>
              {translate('checkout.create_account')}
            </a>
          </div>
        </div>
      </article>
    </section>
  );
};

export default ComprarComoInvitado;
